import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Wallet, Bell, Unlock, Store, RefreshCw, Loader2, Clock, CheckCircle2, AlertCircle, Save, Star, MapPin, ShieldCheck } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import api from "../lib/api";
import { vendorApi, vendorStore } from "../lib/vendorApi";

const STATUS_CONF = {
  pending_admin_review: { icon: Clock, color: "var(--gold-dark)", bg: "rgba(200,162,74,0.10)", border: "var(--gold)",
    title: "Pending Admin Review",
    body: "Your business profile is under review. Approval typically completes within 24-48 hours. You'll receive a notification once approved." },
  approved: { icon: CheckCircle2, color: "var(--leaf)", bg: "rgba(85,127,74,0.08)", border: "var(--leaf)",
    title: "Verified Vendor",
    body: "You are active and receiving live leads based on your city and PIN." },
  rejected: { icon: AlertCircle, color: "var(--crimson)", bg: "rgba(122,14,24,0.06)", border: "var(--crimson)",
    title: "Application Rejected",
    body: "Your profile was declined. Please contact support for details." },
};

const StatusBanner = ({ status }) => {
  const conf = STATUS_CONF[status];
  if (!conf) return null;
  const Icon = conf.icon;
  return (
    <div data-testid={`vendor-status-${status}`} className="p-5 mt-6" style={{ background: conf.bg, border: `1px solid ${conf.border}` }}>
      <div className="flex items-start gap-3">
        <Icon size={20} style={{ color: conf.color, flexShrink: 0 }}/>
        <div>
          <p className="font-serif text-lg" style={{ color: conf.color }}>{conf.title}</p>
          <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>{conf.body}</p>
        </div>
      </div>
    </div>
  );
};

/**
 * Vendor Dashboard — Post-login control room.
 * - Shows status banner (pending / approved / rejected)
 * - Wallet balance + Recharge (Razorpay placeholder)
 * - Notification Center
 * - Assigned Leads table with 1-coin Unlock action
 */
export default function VendorDashboard() {
  const nav = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [wallet, setWallet] = useState({ balance: 0, ledger: [] });
  const [leads, setLeads] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [busy, setBusy] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);

  // Registration/profile form state (top of dashboard) — mobile-app parity fields
  const [form, setForm] = useState({
    business_name: "",
    category: "",
    mobile: "",
    city: "",
    pin: "",
    specialization: "",
    starting_price: "",
    experience_years: "",
    languages: [],     // ["Hindi", "Bhojpuri", …]
    contact_email: "",
    pan_number: "",
    gstin: "",
    pan_card_filename: "",
    description: "",
  });
  const [languageInput, setLanguageInput] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  // Category master list — fetched from /api/guest/vendors/categories on mount
  const [categoryOptions, setCategoryOptions] = useState([]);

  // Peer catalog (bottom of dashboard)
  const [catalog, setCatalog] = useState([]);
  const currency = useMemo(() => (n) => new Intl.NumberFormat("en-IN").format(n), []);

  const load = async () => {
    setBusy(true);
    try {
      const [me, w, n, l, cat, catList] = await Promise.all([
        vendorApi.get("/vendor/me"),
        vendorApi.get("/vendor/wallet"),
        vendorApi.get("/vendor/notifications"),
        vendorApi.get("/vendor/leads").catch(() => ({ data: { leads: [] } })),
        api.get("/guest/vendors").catch(() => ({ data: { vendors: [] } })),
        api.get("/guest/vendors/categories").catch(() => ({ data: { categories: [] } })),
      ]);
      setVendor(me.data);
      setWallet(w.data);
      setNotifs(n.data);
      setLeads(l.data.leads || []);
      setCatalog(cat.data.vendors || []);
      setCategoryOptions((catList.data.categories || []).map((c) => c.name));
      // Seed the profile form with the current vendor record (edit-in-place)
      setForm({
        business_name: me.data.business_name || "",
        category: me.data.category || "",
        mobile: (me.data.mobile || "").startsWith("web-") ? "" : (me.data.mobile || ""),
        city: me.data.city || "",
        pin: me.data.pin || "",
        specialization: me.data.specialization || "",
        starting_price: me.data.starting_price ? String(me.data.starting_price) : "",
        experience_years: me.data.experience_years ? String(me.data.experience_years) : "",
        languages: Array.isArray(me.data.languages) ? me.data.languages : [],
        contact_email: me.data.contact_email || "",
        pan_number: me.data.pan_number || "",
        gstin: me.data.gstin || "",
        pan_card_filename: me.data.pan_card_filename || "",
        description: me.data.description || "",
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        vendorStore.clear();
        // Session token invalidated — auto-reboot into a fresh zero-friction session.
        try {
          const { data } = await vendorApi.post("/vendor/session-init");
          vendorStore.save(data.token, data.vendor);
          window.location.reload();
          return;
        } catch { nav("/"); }
      }
    } finally { setBusy(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = {
        ...form,
        starting_price: form.starting_price ? parseInt(form.starting_price, 10) : undefined,
        experience_years: form.experience_years ? parseInt(form.experience_years, 10) : undefined,
      };
      const { data } = await vendorApi.patch("/vendor/profile", payload);
      setVendor(data.vendor);
      vendorStore.save(vendorStore.token(), data.vendor);
      toast.success("Business profile saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSavingProfile(false); }
  };

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addLanguage = () => {
    const val = languageInput.trim();
    if (!val) return;
    if (form.languages.includes(val)) { setLanguageInput(""); return; }
    setForm({ ...form, languages: [...form.languages, val] });
    setLanguageInput("");
  };
  const removeLanguage = (lang) => {
    setForm({ ...form, languages: form.languages.filter((l) => l !== lang) });
  };
  const handlePanUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm({ ...form, pan_card_filename: file.name });
    toast.info(`PAN card selected: ${file.name} (upload wires with object storage)`);
  };

  useEffect(() => {
    const boot = async () => {
      if (!vendorStore.token()) {
        try {
          const { data } = await vendorApi.post("/vendor/session-init");
          vendorStore.save(data.token, data.vendor);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not initialize vendor session");
          nav("/");
          return;
        }
      }
      await load();
    };
    boot();
    // load once on mount only
  }, []);

  const logout = () => { vendorStore.clear(); nav("/"); };

  const unlock = async (leadId) => {
    try {
      const { data } = await vendorApi.post(`/vendor/leads/${leadId}/unlock`);
      toast.success(data.already_unlocked ? "Already unlocked" : `Unlocked · 1 coin used`);
      await load();
    } catch (err) {
      if (err?.response?.status === 402) {
        toast.error("Insufficient credits. Recharge to continue.");
        setShowRecharge(true);
      } else {
        toast.error(err?.response?.data?.detail || "Unlock failed");
      }
    }
  };

  const recharge = async (tierOrAmount) => {
    try {
      const body = typeof tierOrAmount === "string"
        ? { tier: tierOrAmount }
        : { tier: "low" };
      const { data: order } = await vendorApi.post("/vendor/wallet/recharge", body);

      // Mock-mode fallback for preview environments without Razorpay keys.
      if (order.gateway === "mock" || !order.key_id) {
        toast.success(`Wallet credited · +${order.coins_added ?? 5} coins`);
        setShowRecharge(false); await load(); return;
      }

      // Wait for Razorpay SDK (loaded from public/index.html)
      const Razorpay = await new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve(window.Razorpay);
        let n = 0;
        const t = setInterval(() => {
          if (window.Razorpay) { clearInterval(t); resolve(window.Razorpay); }
          if (++n > 50) { clearInterval(t); reject(new Error("Razorpay SDK failed")); }
        }, 100);
      });

      const options = {
        key: order.key_id,
        amount: order.amount * 100,
        currency: order.currency,
        order_id: order.order_id,
        name: "Vedika Sanskritik Bandhan",
        description: `Vendor Wallet Recharge · ${order.tier || "custom"} tier`,
        theme: { color: "#7A0E18" },
        handler: async (response) => {
          try {
            const { data: v } = await vendorApi.post("/vendor/wallet/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(`Wallet credited · +${v.coins_added} coins`);
            setShowRecharge(false); await load();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed");
          }
        },
        modal: { ondismiss: () => toast.info("Payment cancelled") },
      };
      new Razorpay(options).open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Recharge failed");
    }
  };

  const markRead = async (id) => {
    await vendorApi.post(`/vendor/notifications/${id}/read`);
    load();
  };

  if (!vendor) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--ivory)" }}>
        <Loader2 className="animate-spin" size={20} style={{ color: "var(--crimson)" }}/>
      </div>
    );
  }

  const approved = vendor.status === "approved";

  return (
    <div data-testid="vendor-dashboard" className="min-h-screen w-full flex flex-col" style={{ background: "var(--ivory)" }}>
      <header className="px-6 md:px-10 pt-6 pb-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--line)" }}>
        <BrandLogo size="sm"/>
        <div className="flex items-center gap-4">
          <button onClick={load} disabled={busy} className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
            <RefreshCw size={13} className={busy ? "animate-spin" : ""}/> Refresh
          </button>
          <button onClick={logout} data-testid="vendor-logout" className="text-xs uppercase tracking-widest inline-flex items-center gap-1.5" style={{ color: "var(--ash)" }}>
            <LogOut size={13}/> Logout
          </button>
        </div>
      </header>

      <section className="px-6 md:px-10 pt-8">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow flex items-center gap-2"><Store size={12}/> Vendor Portal</div>
          <h1 className="font-serif text-3xl md:text-4xl mt-2" style={{ color: "var(--crimson)" }} data-testid="vendor-business-name">
            {vendor.business_name}
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>
            {vendor.category} · {vendor.city} · PIN {vendor.pin}
          </p>

          <StatusBanner status={vendor.status}/>
        </div>
      </section>

      {/* Vendor Registration Form — Mobile App Parity (SIMPLE, no rate cards) */}
      <section className="px-6 md:px-10 mt-10" data-testid="vendor-registration-form">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow">Business Profile</div>
          <h2 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--ink)" }}>
            Register / Update Your Business
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>
            Mirrors the bharat-register mobile app — one clean form, one starting price. No rate-card grids.
          </p>

          <form onSubmit={saveProfile} className="mt-6 p-6 md:p-7 space-y-6"
            style={{ background: "var(--bone)", border: "1px solid var(--gold)" }}>

            {/* Business identity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="input-label">Business Name</label>
                <input data-testid="reg-business" required value={form.business_name} onChange={setF("business_name")}
                  placeholder="e.g., Sharma Caterers" className="underline-input"/>
              </div>
              <div>
                <label className="input-label">Service Category</label>
                <select data-testid="reg-category" required value={form.category} onChange={setF("category")}
                  className="underline-input" style={{ background: "transparent" }}>
                  <option value="">— Select a category —</option>
                  {categoryOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="input-label">Mobile</label>
                <input data-testid="reg-mobile" required pattern="[0-9]{10}" value={form.mobile} onChange={setF("mobile")}
                  placeholder="10-digit mobile" className="underline-input"/>
              </div>
              <div>
                <label className="input-label">Business Email</label>
                <input data-testid="reg-email" type="email" value={form.contact_email} onChange={setF("contact_email")}
                  placeholder="business@example.com" className="underline-input"/>
              </div>
              <div>
                <label className="input-label">City</label>
                <input data-testid="reg-city" required value={form.city} onChange={setF("city")}
                  placeholder="Bettiah" className="underline-input"/>
              </div>
              <div>
                <label className="input-label">PIN Code</label>
                <input data-testid="reg-pin" required pattern="[0-9]{6}" value={form.pin} onChange={setF("pin")}
                  placeholder="6-digit" className="underline-input"/>
              </div>
            </div>

            {/* Specialization + pricing + experience */}
            <div>
              <label className="input-label">Specialization</label>
              <textarea data-testid="reg-specialization" value={form.specialization} onChange={setF("specialization")}
                placeholder="e.g., Mandap setup, Stage decor, Fresh floral entry gates, LED backdrop"
                rows={3} className="underline-input" style={{ resize: "vertical", minHeight: "72px" }}/>
              <p className="mt-1 text-[10px]" style={{ color: "var(--ash)" }}>
                A single line describing what you specialize in — this appears on your vendor card.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="input-label">Starting Price (₹)</label>
                <input data-testid="reg-starting-price" type="number" min="0" step="500"
                  value={form.starting_price} onChange={setF("starting_price")}
                  placeholder="e.g., 25000" className="underline-input"/>
                <p className="mt-1 text-[10px]" style={{ color: "var(--ash)" }}>Displayed as &quot;₹X onwards&quot; on listings.</p>
              </div>
              <div>
                <label className="input-label">Years of Experience</label>
                <input data-testid="reg-experience" type="number" min="0" max="80"
                  value={form.experience_years} onChange={setF("experience_years")}
                  placeholder="e.g., 12" className="underline-input"/>
              </div>
            </div>

            {/* Languages — chip input */}
            <div>
              <label className="input-label">Languages Spoken</label>
              <div className="flex flex-wrap gap-2 mt-2" data-testid="reg-languages-chips">
                {form.languages.map((lang) => (
                  <span key={lang} data-testid={`lang-chip-${lang}`}
                    className="inline-flex items-center gap-1.5 text-[11px] tracking-widest uppercase px-2.5 py-1"
                    style={{ background: "rgba(200,162,74,0.15)", border: "1px solid var(--gold)", color: "var(--ink)" }}>
                    {lang}
                    <button type="button" onClick={() => removeLanguage(lang)} style={{ color: "var(--crimson)" }}>×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <input data-testid="reg-language-input" value={languageInput}
                  onChange={(e) => setLanguageInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLanguage(); } }}
                  placeholder="Hindi, Bhojpuri, English…" className="underline-input flex-1"/>
                <button type="button" data-testid="reg-language-add" onClick={addLanguage}
                  className="btn-secondary text-[11px] px-3">Add</button>
              </div>
            </div>

            {/* KYC block */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4" style={{ borderTop: "1px solid var(--line)" }}>
              <div>
                <label className="input-label">PAN Number</label>
                <input data-testid="reg-pan" value={form.pan_number} onChange={setF("pan_number")}
                  placeholder="ABCDE1234F" maxLength={10} className="underline-input uppercase"/>
              </div>
              <div>
                <label className="input-label">GSTIN</label>
                <input data-testid="reg-gstin" value={form.gstin} onChange={setF("gstin")}
                  placeholder="22ABCDE1234F1Z5" maxLength={15} className="underline-input uppercase"/>
              </div>
              <div className="md:col-span-2">
                <label className="input-label">PAN Card Upload</label>
                <label htmlFor="pan-file" data-testid="reg-pan-upload"
                  className="underline-input flex items-center gap-2 cursor-pointer"
                  style={{ paddingTop: "0.6rem", paddingBottom: "0.6rem" }}>
                  <Save size={14} style={{ color: "var(--crimson)" }}/>
                  <span style={{ color: form.pan_card_filename ? "var(--ink)" : "var(--ash)" }}>
                    {form.pan_card_filename || "Click to attach PAN card image (JPG/PDF)"}
                  </span>
                </label>
                <input id="pan-file" type="file" accept="image/*,application/pdf"
                  onChange={handlePanUpload} className="hidden"/>
              </div>
            </div>

            {/* About / description */}
            <div>
              <label className="input-label">About Your Business</label>
              <textarea data-testid="reg-description" value={form.description} onChange={setF("description")}
                placeholder="Coverage areas, USPs, awards, notable weddings served…"
                rows={3} className="underline-input" style={{ resize: "vertical", minHeight: "72px" }}/>
            </div>

            <div className="flex justify-end">
              <button data-testid="reg-save" type="submit" disabled={savingProfile} className="btn-primary">
                {savingProfile ? "Saving…" : "Save Profile"} <Save size={16}/>
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Wallet + Notifications */}
      <section className="px-6 md:px-10 mt-10">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Wallet */}
          <div className="editorial-card p-6 lg:col-span-1" data-testid="vendor-wallet-card">
            <div className="flex items-center gap-2">
              <Wallet size={16} style={{ color: "var(--crimson)" }}/>
              <div className="eyebrow">Credit Wallet</div>
            </div>
            <p className="font-serif mt-4" style={{ color: "var(--crimson)", fontSize: "3rem", lineHeight: 1 }} data-testid="vendor-balance">
              {wallet.balance}
            </p>
            <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>coins available</p>
            <button data-testid="vendor-recharge-btn" onClick={() => setShowRecharge(true)} className="btn-primary mt-5 w-full justify-center">
              Recharge <Wallet size={14}/>
            </button>
            <p className="mt-3 text-[11px]" style={{ color: "var(--ash)" }}>
              1 coin unlocks one lead&apos;s mobile number.
            </p>
          </div>

          {/* Notifications */}
          <div className="editorial-card p-6 lg:col-span-2" data-testid="vendor-notifications-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={16} style={{ color: "var(--crimson)" }}/>
                <div className="eyebrow">Notifications</div>
              </div>
              {notifs.unread > 0 && (
                <span className="text-[10px] tracking-widest uppercase px-2 py-1"
                  style={{ background: "var(--crimson)", color: "#fff" }} data-testid="vendor-unread-count">
                  {notifs.unread} new
                </span>
              )}
            </div>
            <div className="mt-4 divide-y" style={{ borderColor: "var(--line)" }}>
              {notifs.items.length === 0 && (
                <p className="text-sm py-6 text-center" style={{ color: "var(--ash)" }}>No notifications yet.</p>
              )}
              {notifs.items.map((n) => (
                <div key={n.id} className="py-3 flex items-start gap-3">
                  <span className="mt-1 w-2 h-2 rounded-full"
                    style={{ background: n.read ? "var(--line)" : "var(--crimson)", flexShrink: 0 }}/>
                  <div className="flex-1">
                    <p className="font-serif text-base" style={{ color: "var(--ink)" }}>{n.title}</p>
                    <p className="text-sm" style={{ color: "var(--ash)" }}>{n.body}</p>
                    <p className="text-[10px] mt-1" style={{ color: "var(--ash)" }}>{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  {!n.read && (
                    <button onClick={() => markRead(n.id)} className="text-[10px] tracking-widest uppercase" style={{ color: "var(--crimson)" }}>
                      Mark read
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leads */}
      <section className="px-6 md:px-10 mt-10 mb-16">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow">Assigned Leads</div>
          <h2 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--ink)" }}>
            Incoming Client Enquiries
          </h2>
          {!approved && (
            <p className="mt-4 text-sm" style={{ color: "var(--ash)" }}>
              Leads unlock once your profile is approved.
            </p>
          )}
          {approved && (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full text-sm border-collapse" data-testid="vendor-leads-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--line)" }}>
                    <th className="text-left py-3 pr-4 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Received</th>
                    <th className="text-left py-3 pr-4 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>City · PIN</th>
                    <th className="text-left py-3 pr-4 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Mobile</th>
                    <th className="text-left py-3 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr><td colSpan={4} className="py-8 text-center" style={{ color: "var(--ash)" }}>
                      No leads yet. New enquiries in your city will appear here.
                    </td></tr>
                  )}
                  {leads.map((l) => (
                    <tr key={l.id} style={{ borderBottom: "1px solid var(--line)" }}>
                      <td className="py-3 pr-4" style={{ color: "var(--ash)" }}>{new Date(l.created_at).toLocaleString()}</td>
                      <td className="py-3 pr-4" style={{ color: "var(--ink)" }}>{l.city} · {l.pin}</td>
                      <td className="py-3 pr-4 font-mono" style={{ color: l.unlocked ? "var(--crimson)" : "var(--ash)" }}>
                        {l.unlocked ? l.mobile : l.mobile_masked}
                      </td>
                      <td className="py-3">
                        {l.unlocked
                          ? <span className="text-[10px] tracking-widest uppercase" style={{ color: "var(--leaf)" }}>Unlocked</span>
                          : <button data-testid={`unlock-${l.id}`} onClick={() => unlock(l.id)} className="btn-primary text-[11px] py-2 px-3">
                              <Unlock size={12}/> Unlock · 1 coin
                            </button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Peer Vendor Gallery (BOTTOM — mirrors bharat-register mock catalog) */}
      <section className="px-6 md:px-10 mt-4 mb-16" data-testid="vendor-peer-catalog">
        <div className="max-w-6xl mx-auto">
          <div className="eyebrow">Marketplace · Peer Network</div>
          <h2 className="font-serif text-2xl md:text-3xl mt-2" style={{ color: "var(--ink)" }}>
            Wedding Vendor Catalog
          </h2>
          <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>
            Identical to the bharat-register mobile app directory. This mock data will be flushed at go-live.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="peer-grid">
            {catalog.slice(0, 18).map((v) => (
              <article key={v.slug} className="editorial-card p-6 flex flex-col h-full" data-testid={`peer-${v.slug}`}>
                <div className="flex items-start justify-between">
                  <div className="eyebrow">{v.category}</div>
                  {v.verified && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase" style={{ color: "var(--leaf)" }}>
                      <ShieldCheck size={12}/> Verified
                    </span>
                  )}
                </div>
                <h3 className="font-serif text-xl mt-3" style={{ color: "var(--ink)" }}>{v.business_name}</h3>
                {v.specialization && (
                  <p className="mt-1 text-sm" style={{ color: "var(--ash)" }}>{v.specialization}</p>
                )}
                <div className="mt-3 flex items-center gap-3 text-xs">
                  <span className="inline-flex items-center gap-1" style={{ color: "var(--crimson)" }}>
                    <Star size={13} fill="currentColor" strokeWidth={0}/> {v.rating}
                    <span style={{ color: "var(--ash)" }}> · {v.reviews}</span>
                  </span>
                  <span className="inline-flex items-center gap-1" style={{ color: "var(--ash)" }}>
                    <MapPin size={12}/> {v.city}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(v.badges || []).slice(0, 3).map((h) => (
                    <span key={h} className="text-[10px] tracking-widest uppercase px-2 py-1"
                      style={{ background: "rgba(200, 162, 74, 0.10)", border: "1px solid rgba(200, 162, 74, 0.35)", color: "var(--ink)" }}>
                      {h}
                    </span>
                  ))}
                </div>
                <div className="mt-auto pt-4" style={{ borderTop: "1px solid var(--line)", marginTop: "1rem" }}>
                  <p className="text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>Starting Price</p>
                  <p className="font-serif text-lg" style={{ color: "var(--crimson)" }}>
                    ₹{currency(v.starting_price || 0)}
                    <span className="text-[10px] tracking-widest uppercase font-sans ml-1" style={{ color: "var(--ash)" }}>
                      onwards
                    </span>
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Recharge modal */}
      {showRecharge && (
        <div data-testid="recharge-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(26,20,16,0.72)" }}>
          <div className="w-full max-w-md p-7" style={{ background: "var(--ivory)", border: "1px solid var(--gold)" }}>
            <div className="eyebrow">Recharge Wallet</div>
            <h3 className="font-serif text-2xl mt-2" style={{ color: "var(--crimson)" }}>Add Credits</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--ash)" }}>
              Every recharge (any tier) grants a flat <strong>+5 coins</strong>, added cumulatively to your wallet. Razorpay Test Mode.
            </p>

            <div className="mt-5 grid grid-cols-3 gap-2">
              {[["low","₹99","Starter"],["mid","₹299","Preferred"],["high","₹999","Premium"]].map(([t,price,label]) => (
                <button key={t} data-testid={`recharge-tier-${t}`} onClick={() => recharge(t)}
                  className="editorial-card p-3 text-left" style={{ borderColor: "var(--line)" }}>
                  <p className="font-serif text-lg" style={{ color: "var(--crimson)" }}>{price}</p>
                  <p className="mt-1 text-[10px] tracking-widest uppercase" style={{ color: "var(--ash)" }}>{label}</p>
                  <p className="mt-2 text-[11px]" style={{ color: "var(--leaf)" }}>+5 coins</p>
                </button>
              ))}
            </div>

            <div className="mt-6 flex gap-3">
              <button onClick={() => setShowRecharge(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
